import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-step-up-gate.css?inline';
import {
    STEP_UP_REQUEST_EVENT, STEP_UP_TOKEN_FIELD, formFields, sendAction, stepUpDemand,
    type StepUpAction, type StepUpOutcome, type StepUpRequestDetail,
} from '../../lib/step-up.js';
import '../pk-alert/pk-alert.js';
import '../pk-modal/pk-modal.js';
import '../pk-totp-reprompt/pk-totp-reprompt.js';
import '../pk-totp-setup-card/pk-totp-setup-card.js';

/**
 * `<pk-step-up-gate>` — wraps actions that the API may gate behind a fresh TOTP (contract `x-step-up`).
 *
 * It runs an action (a form-encoded POST with `Accept: application/json`); when the answer is
 * `403 { error: "step-up-required" }` it shows the kit's standard `<pk-totp-reprompt>` (the same prompt the staff
 * pages use), exchanges the code at `verify-url` for a short-lived proof token, and re-dispatches the same action
 * with the token in the `stepUpToken` field. When the answer says `setupNeeded` (no TOTP enrolled) it shows
 * `<pk-totp-setup-card>` pointing at `setup-url` instead. Actions that need no step-up pass straight through.
 *
 * Actions reach the gate three ways:
 *   - a light-DOM `<form data-pk-step-up>` inside the gate (its submit is intercepted);
 *   - a `pk-step-up-request` event from a kit component inside it (`requestThroughGate()` — pk-key-table,
 *     pk-destroy-key-flow, pk-escrow-recover-modal, …), claimed across shadow roots;
 *   - `gate.run({ url, fields, label })` from script.
 *
 * On success the gate follows `redirect` from the JSON answer (unless `no-redirect`); on a refusal it shows the
 * answer's `message` in an error alert.
 *
 * Attributes:
 *   - `verify-url`   — POST endpoint for the code (`{ code, action, targetId, correlationId }` → `{ token }`). Required.
 *   - `setup-url`    — the portal's TOTP setup page (shown when `setupNeeded`).
 *   - `action-label` — default prompt label when an action carries none.
 *   - `token-field`  — form field of the proof on the retry (default `stepUpToken`).
 *   - `no-redirect`  — boolean; do not follow `redirect` (the page handles `pk-step-up-complete`).
 *
 * Events:
 *   - `pk-step-up-complete`  — `{ status, data, url }` after a 2xx answer.
 *   - `pk-step-up-cancelled` — `{ url, reason: 'cancelled' | 'setup-needed' }` when the user backs out or has no TOTP.
 *   - `pk-step-up-error`     — `{ status, error, message, url }` when the host refused.
 */
export class PkStepUpGate extends HTMLElement {
    static readonly tagName = 'pk-step-up-gate';

    static get observedAttributes(): string[] {
        return ['verify-url', 'setup-url', 'action-label', 'token-field', 'no-redirect'];
    }

    private readonly _root: ShadowRoot;
    private _error = '';
    private _setupOpen = false;
    private _promptOpen = false;
    private _promptLabel = '';
    private _pendingPrompt: ((token: string | null) => void) | null = null;
    private _busy = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.addEventListener(STEP_UP_REQUEST_EVENT, this._onRequest as EventListener);
        this.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this.removeEventListener(STEP_UP_REQUEST_EVENT, this._onRequest as EventListener);
        this.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(): void {
        if (this.isConnected) this._render();
    }

    /** True while an action is in flight. */
    get busy(): boolean {
        return this._busy;
    }

    /** The last refusal shown by the gate ('' when none). */
    get error(): string {
        return this._error;
    }

    /** Follows a `redirect` from a successful answer. Overridable (tests, SPA hosts). */
    navigate(url: string): void {
        window.location.assign(url);
    }

    /** Runs an action through the step-up round trip. */
    async run(action: StepUpAction): Promise<StepUpOutcome> {
        this._busy = true;
        this._error = '';
        this._render();
        try {
            return await this._run(action);
        } finally {
            this._busy = false;
            this._render();
        }
    }

    private async _run(action: StepUpAction): Promise<StepUpOutcome> {
        const tokenField = this.getAttribute('token-field') || STEP_UP_TOKEN_FIELD;
        let response = await sendAction(action, null, tokenField);
        let demand = stepUpDemand(response);

        if (demand.required) {
            if (demand.setupNeeded) {
                this._setupOpen = true;
                this._render();
                this._emit('pk-step-up-cancelled', { url: action.url, reason: 'setup-needed' });
                return { ok: false, status: response.status, data: response.data, cancelled: true, error: 'step-up-required' };
            }
            const token = await this._prompt(action.label || this.getAttribute('action-label') || 'Confirm this action');
            if (!token) {
                this._emit('pk-step-up-cancelled', { url: action.url, reason: 'cancelled' });
                return { ok: false, status: response.status, data: response.data, cancelled: true, error: 'step-up-required' };
            }
            response = await sendAction(action, token, tokenField);
            demand = stepUpDemand(response);
        }

        const ok = response.status >= 200 && response.status < 300;
        if (ok) {
            this._emit('pk-step-up-complete', { status: response.status, data: response.data, url: action.url });
            const redirect = typeof response.data?.['redirect'] === 'string' ? response.data['redirect'] as string : '';
            if (redirect && !this.hasAttribute('no-redirect')) this.navigate(redirect);
            return { ok: true, status: response.status, data: response.data, cancelled: false };
        }

        const slug = typeof response.data?.['error'] === 'string' ? response.data['error'] as string : 'unavailable';
        const message = typeof response.data?.['message'] === 'string' && response.data['message']
            ? response.data['message'] as string
            : demand.required
                ? 'The authenticator code was not accepted. Try again.'
                : 'That did not work. Try again.';
        this._error = message;
        this._emit('pk-step-up-error', { status: response.status, error: slug, message, url: action.url });
        return { ok: false, status: response.status, data: response.data, cancelled: false, error: slug };
    }

    private _prompt(label: string): Promise<string | null> {
        this._promptLabel = label;
        this._promptOpen = true;
        this._render();
        return new Promise((resolve) => { this._pendingPrompt = resolve; });
    }

    private _settlePrompt(token: string | null): void {
        this._promptOpen = false;
        const pending = this._pendingPrompt;
        this._pendingPrompt = null;
        this._render();
        pending?.(token);
    }

    private readonly _onVerified = (e: Event): void => {
        e.stopPropagation();
        this._settlePrompt(((e as CustomEvent<{ token?: string }>).detail?.token) || null);
    };

    private readonly _onPromptCancelled = (e: Event): void => {
        e.stopPropagation();
        this._settlePrompt(null);
    };

    private readonly _onRequest = (e: CustomEvent<StepUpRequestDetail>): void => {
        e.preventDefault();
        e.stopPropagation();
        const { settle, ...action } = e.detail;
        void this.run(action).then(settle);
    };

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-pk-step-up')) return;
        e.preventDefault();
        void this.run({
            url: form.getAttribute('action') ?? '',
            method: form.getAttribute('method') ?? 'POST',
            fields: formFields(form),
            label: form.getAttribute('data-pk-step-up-label') ?? undefined,
        });
    };

    private readonly _closeSetup = (): void => {
        this._setupOpen = false;
        this._render();
    };

    private _emit(name: string, detail: Record<string, unknown>): void {
        this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
    }

    private template(): TemplateResult {
        const setupUrl = this.getAttribute('setup-url') ?? '';
        return html`
            <style>${styles}</style>
            ${this._error ? html`<pk-alert variant="error" class="gate-error" role="alert">${this._error}</pk-alert>` : nothing}
            <slot></slot>
            <pk-totp-reprompt
                class="gate-prompt"
                ?open=${this._promptOpen}
                verify-url=${this.getAttribute('verify-url') ?? ''}
                action="portal.step-up"
                target-id="step-up"
                action-label=${this._promptLabel}
                title="Confirm with your authenticator code"
                @pk-totp-verified=${this._onVerified}
                @pk-totp-cancelled=${this._onPromptCancelled}
            ></pk-totp-reprompt>
            ${this._setupOpen ? html`
                <pk-modal class="gate-setup" open title="Two-factor authentication required" @pk-cancel=${this._closeSetup}>
                    <pk-totp-setup-card
                        action=${setupUrl}
                        title="Set up two-factor authentication first"
                        lede="This action needs a fresh authenticator code, and your account has no authenticator yet. Set one up, then try again."
                    >
                        ${setupUrl ? html`<a slot="foot" class="setup-link" href=${setupUrl}>Open two-factor setup</a>` : nothing}
                    </pk-totp-setup-card>
                </pk-modal>
            ` : nothing}
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkStepUpGate.tagName)) {
    customElements.define(PkStepUpGate.tagName, PkStepUpGate);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-step-up-gate': PkStepUpGate;
    }
}
